import ReactMarkdown from 'react-markdown';
import { cn } from '@/utils';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Markdown, and nothing else: forum post and comment bodies, and the job
 * page's Markdown fields.
 *
 * react-markdown runs without a raw-HTML plugin, so HTML in the content, typed
 * or entity-encoded, shows as text and is never parsed into elements.
 * Character references the backend stored in prose, such as `&amp;`, still
 * display as the character, because Markdown decodes them into text (code
 * spans and code blocks show them as written). Link and image URLs go through
 * react-markdown's default filter, which keeps relative URLs and http(s),
 * mailto, irc(s) and xmpp only.
 *
 * Forum users write these bodies. Keep raw-HTML plugins such as rehype-raw,
 * and any injection of an HTML string, out of this component.
 */
export function MarkdownContent({ content, className }: MarkdownContentProps) {
  return (
    // overflow-wrap: a pasted URL or long token breaks onto the next line
    // instead of pushing the post past a phone's clipped edge. It only acts on
    // a word wider than the line (T101026002).
    <div className={cn("prose prose-sm dark:prose-invert max-w-none [overflow-wrap:anywhere]", className)}>
      <ReactMarkdown
        components={{
          // Customize code blocks
          code: ({ className, children, ...props }: any) => {
            const isInline = !className || !className.includes('language-');
            if (isInline) {
              return (
                <code className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className="block p-3 rounded-lg bg-muted text-sm font-mono overflow-x-auto" {...props}>
                {children}
              </code>
            );
          },
          // Customize headings
          h1: ({ children }) => <h1 className="text-2xl font-extrabold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-bold mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold mt-2 mb-1">{children}</h3>,
          // Customize lists
          ul: ({ children }) => <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>,
          // Customize paragraphs
          p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
          // Customize links
          a: ({ href, children }) => (
            <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          // Customize blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-primary/30 pl-4 my-2 italic text-foreground/70">
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

